import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  ALERT,
  doCreateComplianceType,
  doUpdateComplianceType,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { cloneDeep, isEqual } from 'lodash';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { PageHeader } from 'components/PageHeader';
import { selectRemoteComplianceSettingComplianceType } from 'store/remote/selectors';
import { selectUserSelectedOrganisationId } from 'store/auth/selectors';
import { useSelector } from 'hooks/useSelector';

export const ComplianceTypeModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsComplianceTypeModal>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const confirmUnsaved = useConfirmUnsaved();

  const { complianceTypeId, eventKey } = route.params;

  const complianceType = useSelector((state) =>
    complianceTypeId
      ? selectRemoteComplianceSettingComplianceType(state, complianceTypeId)
      : undefined,
  );

  const organisationId = useSelector(selectUserSelectedOrganisationId)!;

  const initDataRef = React.useRef(
    cloneDeep(
      complianceType ?? {
        complianceTypeId: complianceTypeId ?? undefined,
        name: '',
      },
    ),
  );
  const dataRef = React.useRef(cloneDeep(initDataRef.current));
  const isDirty = React.useCallback(
    () => !isEqual(initDataRef.current, dataRef.current),
    [],
  );
  const isDirtyRef = React.useRef<boolean>(false);
  isDirtyRef.current = isDirty();

  const handleSave = React.useCallback(() => {
    if (dataRef.current.name.length >= 60) {
      dispatch(ALERT({ text: 'Name must be less than 60 characters' }));
      return;
    } else if (dataRef.current.name.length === 0) {
      dispatch(ALERT({ text: 'Name is required' }));
      return;
    }
    dispatch(
      complianceTypeId
        ? doUpdateComplianceType({
            organisationUid: organisationId,
            eventKey,
            resource: {
              name: dataRef.current.name,
              complianceTypeId,
            },
          })
        : doCreateComplianceType({
            organisationUid: organisationId,
            eventKey,
            resource: {
              name: dataRef.current.name,
            },
          }),
    );
  }, [dispatch, organisationId, complianceTypeId, eventKey]);

  const handleClose = React.useCallback(() => {
    if (isDirty()) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, dispatch, handleSave, isDirty]);

  const handleChangeValue = <K extends keyof typeof dataRef.current>(
    key: K,
    value: (typeof dataRef.current)[K],
    doForceUpdate: boolean = false,
  ) => {
    dataRef.current[key] = value;

    if (doForceUpdate || isDirtyRef.current !== isDirty()) {
      forceUpdate();
    }
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty() });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Compliance Type"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card label="Name" required>
        <TextAreaField
          onChange={(value) => handleChangeValue('name', value)}
          value={dataRef.current.name}
          onEsc={handleClose}
          autoFocusWeb={!complianceTypeId}
          autoFocusDelay
          maxLength={60}
        />
      </Card>
    </Page>
  );
};
